import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { realtime } from '@/lib/realtime';
import { useAuth } from '@/contexts/AuthContext';
import { useNotificationSound } from '@/hooks/useNotificationSound';
import { toast } from 'sonner';

interface ChatContactLite {
  user_id: string;
  nome_completo: string;
}

/**
 * Hook para sincronização realtime de mensagens de chat
 * Segue o padrão de useOrdersSync e outros hooks realtime existentes
 *
 * O servidor emite eventos de chat_messages apenas para as rooms dos dois
 * participantes (user:<id>) — o filtro abaixo é defesa em profundidade.
 * O nome do remetente vem dos caches TanStack já populados pelo useChat
 * (sem fetch paralelo próprio).
 */
export const useChatSync = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { playSound } = useNotificationSound();

  const playSoundRef = useRef(playSound);
  playSoundRef.current = playSound;

  const userId = user?.id;

  useEffect(() => {
    // Dependência em userId (id estável): religa a subscription quando o
    // login acontece após a montagem — antes, com deps [queryClient], o
    // efeito rodava uma vez sem user e o realtime só voltava com F5.
    if (!userId) return;

    const resolveSenderName = (senderId: string): string => {
      const contacts = queryClient.getQueryData<ChatContactLite[]>(['chat-contacts']);
      const fromContacts = contacts?.find((c) => c.user_id === senderId)?.nome_completo;
      if (fromContacts) return fromContacts;
      const convs = queryClient.getQueryData<ChatContactLite[]>(['chat-conversations', userId]);
      return convs?.find((c) => c.user_id === senderId)?.nome_completo || 'Nova mensagem';
    };

    const channel = realtime
      .channel('chat-messages-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `recipient_id=eq.${userId}`,
        },
        (payload) => {
          const newMsg = payload.new as {
            sender_id: string;
            content: string | null;
            attachment_name: string | null;
          };

          playSoundRef.current('chat_message');

          const preview = newMsg.attachment_name
            ? `📎 ${newMsg.attachment_name}`
            : (newMsg.content?.substring(0, 50) || 'Nova mensagem');

          toast.info(`💬 ${resolveSenderName(newMsg.sender_id)}`, {
            description: preview,
            duration: 5000,
          });

          queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
          queryClient.invalidateQueries({ queryKey: ['chat-messages', newMsg.sender_id] });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'chat_messages',
          filter: `sender_id=eq.${userId}`,
        },
        (payload) => {
          // Mensagem foi lida pelo destinatário
          const recipientId = (payload.new as { recipient_id: string }).recipient_id;
          queryClient.invalidateQueries({ queryKey: ['chat-messages', recipientId] });
        }
      )
      .subscribe((status) => {
        console.log('📨 [ChatSync] Status da subscription:', status);
      });

    return () => {
      realtime.removeChannel(channel);
    };
  }, [queryClient, userId]);
};
